// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { Policy } from '../../types/policy';

/**
 * Two-step Change of Agency wizard.
 *
 * <p>Deliberately much smaller than the relationship-creation
 * {@code WizardContext}: CoA only needs (1) policy selection and
 * (2) the client invitation email customisation. The shared
 * {@link StepProgressBar} component renders the stepper.
 */
export const COA_WIZARD_STEPS = [
  { id: 1 as const, name: 'Select The Policies' },
  { id: 2 as const, name: 'Send Letter' },
] as const;

export type CoAWizardStepId = typeof COA_WIZARD_STEPS[number]['id'];

export interface CoAWizardState {
  currentStep: CoAWizardStepId;
  selectedPolicyIds: string[];
  customSubject: string;
  customBody: string;
  isEmailModified: boolean;
  isSubmitting: boolean;
  submitError: string | null;
}

interface CoAWizardContextValue {
  state: CoAWizardState;
  // Step navigation
  goToStep: (step: CoAWizardStepId) => void;
  nextStep: () => void;
  prevStep: () => void;
  canNavigateToStep: (step: CoAWizardStepId) => boolean;
  // Policy selection
  togglePolicy: (policyId: string) => void;
  setSelectedPolicies: (policyIds: string[]) => void;
  clearSelectedPolicies: () => void;
  // Email crafting
  updateEmailContent: (subject: string, body: string) => void;
  resetEmailToDefault: () => void;
  // Submission
  setSubmitting: (submitting: boolean) => void;
  setSubmitError: (error: string | null) => void;
}

const CoAWizardContext = createContext<CoAWizardContextValue | null>(null);

const INITIAL_STATE: CoAWizardState = {
  currentStep: 1,
  selectedPolicyIds: [],
  customSubject: '',
  customBody: '',
  isEmailModified: false,
  isSubmitting: false,
  submitError: null,
};

export type CoAWizardProviderProps = Readonly<{
  children: ReactNode;
  /** Optional initial state — used by tests to start partway through the wizard */
  initialState?: Partial<CoAWizardState>;
}>;

export function CoAWizardProvider({ children, initialState }: CoAWizardProviderProps) {
  const [state, setState] = useState<CoAWizardState>({
    ...INITIAL_STATE,
    ...initialState,
  });

  // ------------------------------------------------------------------ //
  // Validation
  // ------------------------------------------------------------------ //

  const canNavigateToStep = useCallback(
    (step: CoAWizardStepId): boolean => {
      // Step 1 is always reachable
      if (step === 1) return true;
      // Step 2 requires at least one selected policy
      if (step === 2) return state.selectedPolicyIds.length > 0;
      return false;
    },
    [state.selectedPolicyIds.length]
  );

  // ------------------------------------------------------------------ //
  // Navigation
  // ------------------------------------------------------------------ //

  const goToStep = useCallback(
    (step: CoAWizardStepId) => {
      setState((prev) => {
        if (step !== 1 && step !== 2) return prev;
        return { ...prev, currentStep: step };
      });
    },
    []
  );

  const nextStep = useCallback(() => {
    setState((prev) => {
      if (prev.currentStep === 1 && prev.selectedPolicyIds.length > 0) {
        return { ...prev, currentStep: 2 };
      }
      return prev;
    });
  }, []);

  const prevStep = useCallback(() => {
    setState((prev) => {
      if (prev.currentStep === 2) {
        return { ...prev, currentStep: 1 };
      }
      return prev;
    });
  }, []);

  // ------------------------------------------------------------------ //
  // Policy selection
  // ------------------------------------------------------------------ //

  const togglePolicy = useCallback((policyId: string) => {
    setState((prev) => {
      const isSelected = prev.selectedPolicyIds.includes(policyId);
      return {
        ...prev,
        selectedPolicyIds: isSelected
          ? prev.selectedPolicyIds.filter((id) => id !== policyId)
          : [...prev.selectedPolicyIds, policyId],
      };
    });
  }, []);

  const setSelectedPolicies = useCallback((policyIds: string[]) => {
    setState((prev) => ({ ...prev, selectedPolicyIds: [...policyIds] }));
  }, []);

  const clearSelectedPolicies = useCallback(() => {
    setState((prev) => ({ ...prev, selectedPolicyIds: [] }));
  }, []);

  // ------------------------------------------------------------------ //
  // Email crafting
  // ------------------------------------------------------------------ //

  const updateEmailContent = useCallback((subject: string, body: string) => {
    setState((prev) => ({
      ...prev,
      customSubject: subject,
      customBody: body,
      isEmailModified: true,
    }));
  }, []);

  const resetEmailToDefault = useCallback(() => {
    setState((prev) => ({
      ...prev,
      customSubject: '',
      customBody: '',
      isEmailModified: false,
    }));
  }, []);

  // ------------------------------------------------------------------ //
  // Submission
  // ------------------------------------------------------------------ //

  const setSubmitting = useCallback((submitting: boolean) => {
    setState((prev) => ({ ...prev, isSubmitting: submitting }));
  }, []);

  const setSubmitError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, submitError: error, isSubmitting: false }));
  }, []);

  const value = useMemo<CoAWizardContextValue>(
    () => ({
      state,
      goToStep,
      nextStep,
      prevStep,
      canNavigateToStep,
      togglePolicy,
      setSelectedPolicies,
      clearSelectedPolicies,
      updateEmailContent,
      resetEmailToDefault,
      setSubmitting,
      setSubmitError,
    }),
    [
      state,
      goToStep,
      nextStep,
      prevStep,
      canNavigateToStep,
      togglePolicy,
      setSelectedPolicies,
      clearSelectedPolicies,
      updateEmailContent,
      resetEmailToDefault,
      setSubmitting,
      setSubmitError,
    ]
  );

  return <CoAWizardContext.Provider value={value}>{children}</CoAWizardContext.Provider>;
}

export function useCoAWizard(): CoAWizardContextValue {
  const ctx = useContext(CoAWizardContext);
  if (!ctx) {
    throw new Error('useCoAWizard must be used inside a CoAWizardProvider');
  }
  return ctx;
}

/** CoA statuses that indicate an active (in-progress) Change of Agency. */
const ACTIVE_COA_STATUSES = new Set([
  'DRAFT', 'GENERATING', 'PENDING_SIGNATURE', 'PARTIALLY_SIGNED',
  'SIGNED', 'PENDING_PROVIDER',
]);

/**
 * Helper: filter a profile's policies down to those that are eligible for a
 * Change of Agency. A policy is eligible when:
 * - Its agency has not yet been transferred ({@code inAgency !== true})
 * - It does not have an active CoA in progress
 */
export function filterEligibleCoAPolicies(policies: Policy[]): Policy[] {
  return policies.filter(
    (p) => p.inAgency !== true && !ACTIVE_COA_STATUSES.has(p.coaStatus ?? ''),
  );
}

/**
 * Returns the set of policy IDs that are ineligible for CoA selection.
 * Used by the wizard to show these policies as disabled/dimmed rows.
 */
export function getIneligibleCoAPolicyIds(policies: Policy[]): Set<string> {
  const eligible = filterEligibleCoAPolicies(policies);
  const eligibleIds = new Set(eligible.map((p) => p.id));
  return new Set(policies.filter((p) => !eligibleIds.has(p.id)).map((p) => p.id));
}
